// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module errors
 * @package @geenius/analytics
 * @description Defines the typed analytics error hierarchy shared across every
 * package variant. These errors wrap the published `@geenius/errors`
 * primitives so consumers can discriminate analytics-specific failures.
 */

import {
  ErrorCode,
  GeeniusError,
  type GeeniusErrorOptions,
} from "@geenius/errors";

/**
 * Options accepted by the analytics error hierarchy.
 *
 * @property code Optional error code override for specialised analytics errors.
 */
export interface AnalyticsErrorOptions extends GeeniusErrorOptions {
  code?: ErrorCode;
}

/**
 * Base error for analytics-specific failures.
 *
 * @param options Structured error metadata passed to the shared Geenius base error.
 * @returns A typed analytics error instance.
 */
export class AnalyticsError extends GeeniusError {
  constructor(options: AnalyticsErrorOptions) {
    super({
      ...options,
      code: options.code ?? ErrorCode.INTERNAL,
    });
    this.name = "AnalyticsError";
    Object.setPrototypeOf(this, AnalyticsError.prototype);
  }
}

/**
 * Error thrown when analytics configuration is invalid or incomplete.
 *
 * @param options Structured error metadata excluding fixed configuration fields.
 * @returns A typed analytics configuration error.
 */
export class AnalyticsConfigurationError extends AnalyticsError {
  constructor(options: Omit<AnalyticsErrorOptions, "code" | "statusCode">) {
    super({
      ...options,
      code: ErrorCode.INVALID_CONFIG,
      statusCode: 500,
    });
    this.name = "AnalyticsConfigurationError";
    Object.setPrototypeOf(this, AnalyticsConfigurationError.prototype);
  }
}

/**
 * Error thrown when analytics context is accessed outside a provider boundary.
 *
 * @param options Structured error metadata excluding fixed context fields.
 * @returns A typed analytics context error.
 */
export class AnalyticsContextError extends AnalyticsError {
  constructor(options: Omit<AnalyticsErrorOptions, "code" | "statusCode">) {
    super({
      ...options,
      code: ErrorCode.INTERNAL,
      statusCode: 500,
    });
    this.name = "AnalyticsContextError";
    Object.setPrototypeOf(this, AnalyticsContextError.prototype);
  }
}

/**
 * Error thrown when an upstream analytics provider fails or is unavailable.
 *
 * @param options Structured error metadata excluding fixed provider fields.
 * @returns A typed analytics provider error.
 */
export class AnalyticsProviderError extends AnalyticsError {
  constructor(options: Omit<AnalyticsErrorOptions, "code" | "statusCode">) {
    super({
      ...options,
      code: ErrorCode.INTERNAL,
      statusCode: 502,
    });
    this.name = "AnalyticsProviderError";
    Object.setPrototypeOf(this, AnalyticsProviderError.prototype);
  }
}

/**
 * Error thrown when a launch adapter cannot support a relational or provider-
 * specific operation without lying about semantics.
 *
 * @param options Structured error metadata excluding fixed adapter fields.
 * @returns A typed unsupported-adapter error.
 */
export class AdapterUnsupportedError extends AnalyticsError {
  constructor(options: Omit<AnalyticsErrorOptions, "code" | "statusCode">) {
    super({
      ...options,
      code: ErrorCode.INVALID_CONFIG,
      statusCode: 501,
    });
    this.name = "AdapterUnsupportedError";
    Object.setPrototypeOf(this, AdapterUnsupportedError.prototype);
  }
}
